import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  /** La ruta actual: al cambiar, se olvida el fallo y se vuelve a intentar pintar. */
  ruta: string;
  children: ReactNode;
}

interface Estado {
  error: Error | null;
}

/**
 * Contiene el fallo de una pantalla para que no se lleve por delante el CRM.
 *
 * Sin esto, un error al pintar cualquier pantalla —un campo que llega vacío,
 * una lista que no es una lista— hace que React desmonte la aplicación entera.
 * Eso deja la página en blanco y, sobre todo, se lleva el iframe de la
 * centralita que cuelga del layout: la llamada en curso se corta sin aviso.
 * Rodeando solo las pantallas, lo que falla se sustituye por este aviso y el
 * layout, el menú y la llamada siguen donde estaban.
 *
 * Tiene que ser una clase: React no ofrece los límites de error como hook.
 */
export default class FalloPantalla extends Component<Props, Estado> {
  state: Estado = { error: null };

  static getDerivedStateFromError(error: Error): Estado {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Fallo al pintar la pantalla', error, info.componentStack);
  }

  componentDidUpdate(anteriores: Props) {
    // Irse a otra sección es la forma natural de salir del aviso.
    if (this.state.error && anteriores.ruta !== this.props.ruta) this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="max-w-xl mx-auto card text-center py-10 px-5 mt-6">
        <AlertTriangle className="mx-auto text-amber-500 mb-3" size={28} />
        <p className="text-slate-700 dark:text-slate-200 font-medium">Esta pantalla ha fallado</p>
        <p className="text-slate-500 text-sm mt-2">
          El resto del CRM sigue funcionando: puedes ir a otra sección desde el menú. Si estabas en una
          llamada, sigue en curso.
        </p>
        <p className="text-xs text-slate-400 mt-3 break-words">{error.message}</p>
        <button onClick={() => this.setState({ error: null })} className="btn-secondary text-sm mt-4">
          Volver a intentarlo
        </button>
      </div>
    );
  }
}
